import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2, Loader2, X, Gift, Wallet, Shield, Megaphone } from 'lucide-react';
import { notificationService } from '../../services/notifications';

export default function NotificationsDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000); // Every minute
    return () => clearInterval(interval);
  }, []);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const data = await notificationService.getUnreadCount();
      setUnreadCount(data.unread_count);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await notificationService.getAll();
      setNotifications(data.notifications.data || []);
      setUnreadCount(data.unread_count);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    setMarkingAllRead(true);
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev =>
        prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await notificationService.delete(notificationId);
      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (notification && !notification.read_at) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'contribution':
        return <Gift className="w-4 h-4 text-secondary-500" />;
      case 'withdrawal':
        return <Wallet className="w-4 h-4 text-primary-500" />;
      case 'kyc':
        return <Shield className="w-4 h-4 text-violet-500" />;
      case 'campaign':
        return <Megaphone className="w-4 h-4 text-amber-500" />;
      default:
        return <Bell className="w-4 h-4 text-warm-500" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return 'À l\'instant';
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 604800) return `Il y a ${Math.floor(diff / 86400)} j`;
    return date.toLocaleDateString('fr-FR');
  };

  const getNotificationLink = (notification) => {
    const data = notification.data || {};
    if (data.campaign_slug) return `/campaign/${data.campaign_slug}`;
    if (notification.type === 'withdrawal') return '/dashboard/withdrawals';
    if (notification.type === 'kyc') return '/dashboard/settings';
    return null;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-xl hover:bg-[var(--ui-surface-alt)] transition-colors"
        aria-label={unreadCount > 0 ? `Notifications (${unreadCount} non lues)` : 'Notifications'}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <Bell className="w-5 h-5 text-warm-600 dark:text-slate-400" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] app-surface rounded-2xl shadow-2xl shadow-warm-900/20 border app-border overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b app-border bg-gradient-to-r from-primary-50 to-violet-50">
            <h3 className="font-display font-semibold text-warm-900 dark:text-slate-100">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                disabled={markingAllRead}
                className="text-sm text-primary-700 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-400 font-medium flex items-center gap-1"
              >
                {markingAllRead ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCheck className="w-4 h-4" />
                )}
                Tout marquer lu
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
              </div>
            ) : notifications.length > 0 ? (
              <div className="divide-y app-border">
                {notifications.map((notification) => {
                  const link = getNotificationLink(notification);
                  const Content = (
                    <div
                      className={`p-4 hover:bg-warm-50 dark:hover:bg-slate-800 transition-colors ${
                        !notification.read_at ? 'bg-primary-50/30' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          !notification.read_at ? 'app-surface shadow-sm' : 'app-surface-alt'
                        }`}>
                          {getIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm ${
                              !notification.read_at ? 'font-semibold text-warm-900 dark:text-slate-100' : 'text-warm-700 dark:text-slate-300'
                            }`}>
                              {notification.title}
                            </p>
                            <div className="flex items-center gap-1">
                              {!notification.read_at && (
                                <button
                                  onClick={(e) => { e.preventDefault(); handleMarkAsRead(notification.id); }}
                                  className="p-1 text-warm-400 hover:text-primary-500 rounded"
                                  aria-label="Marquer comme lu"
                                  title="Marquer comme lu"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={(e) => { e.preventDefault(); handleDelete(notification.id); }}
                                className="p-1 text-warm-400 hover:text-rose-500 rounded"
                                aria-label="Supprimer la notification"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <p className="text-sm text-warm-600 dark:text-slate-400 mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-warm-400 mt-1">
                            {formatTime(notification.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );

                  return link ? (
                    <Link
                      key={notification.id}
                      to={link}
                      onClick={() => {
                        if (!notification.read_at) handleMarkAsRead(notification.id);
                        setIsOpen(false);
                      }}
                    >
                      {Content}
                    </Link>
                  ) : (
                    <div key={notification.id}>{Content}</div>
                  );
                })}
              </div>
            ) : (
              <div className="py-12 text-center">
                <div className="w-14 h-14 rounded-2xl bg-warm-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
                  <Bell className="w-7 h-7 text-warm-400" />
                </div>
                <p className="text-warm-600 dark:text-slate-400">Aucune notification</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
